import { useEffect, useId, useRef } from 'react'
import { create } from 'zustand'
import { CowboyHat } from './cowboyHat'
import { EmmaEngine, EMMA_COLORS, emmaBus, type EmmaSkin } from './emmaEngine'
import { EmmaVoiceRing } from '@/components/voice/EmmaVoiceRing'
const BRAND_AMBER = '#c8854a'

export const useEmmaUI = create<{ open: boolean; setOpen: (o: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}))

/** The live engine, so the chat panel can drive her through the same rig.
 *  The large Emma in her sheet wins while she exists. */
let current: EmmaEngine | null = null
let sheet: EmmaEngine | null = null
export function emmaEngine() { return sheet ?? current }

function prefersReduced() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

/**
 * Emma: in the phone's tab bar, or in her corner on a desktop. Tap anywhere and
 * she looks at it; scroll and she follows the feed; leave her alone for twenty
 * seconds and she dozes. Tapping her opens the chat.
 */
export function Emma({ color, skin, accent, variant = 'dock', ring = false }: {
  color?: string | null; skin?: EmmaSkin | null; accent?: string | null
  /** The voice ring around her (voice-ring.js): signed-in app chrome only. */
  ring?: boolean
  /** 'tab' sits in the phone's tab bar (Brian: Feed · Library · Record ＋ · Alerts · Emma); 'dock' is the desktop corner. */
  /** 'sheet' is the large Emma at the top of her glass sheet (emma-chat.js v2.1: 124px, twice her resting size). */
  variant?: 'dock' | 'tab' | 'sheet'
}) {
  const uid = 'emma' + useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const svgRef = useRef<SVGSVGElement>(null)
  const engineRef = useRef<EmmaEngine | null>(null)
  const open = useEmmaUI((s) => s.open)
  const setOpen = useEmmaUI((s) => s.setOpen)

  // Default is the locked brand amber; 'accent' wears the account's own colour.
  const fill = color === 'accent' && accent
    ? accent
    : EMMA_COLORS.find((c) => c.id === color || c.hex === color)?.hex ?? BRAND_AMBER

  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const $ = <T extends Element>(id: string) => svg.querySelector(`#${id}`) as T
    const engine = new EmmaEngine({
      svg,
      body: $(`${uid}Body`), over: $(`${uid}Over`), eyeL: $(`${uid}L`), eyeR: $(`${uid}R`),
      clip: $(`${uid}ClipPath`), ridge: $(`${uid}Ridge`), shade: $(`${uid}Shade`), hat: $(`${uid}Hat`),
    }, { skin: skin ?? 'seed', reducedMotion: prefersReduced() })
    engineRef.current = engine
    // Oct 22 (Brian: "awake and alert"): she rests alert, in her sheet and docked.
    engine.setRestAlert(true)
    if (variant === 'sheet') sheet = engine
    else current = engine
    return () => { engine.destroy(); if (current === engine) current = null; if (sheet === engine) sheet = null }
  }, [uid]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { engineRef.current?.setSkin(skin ?? 'seed') }, [skin])

  useEffect(() => {
    if (variant === 'sheet') return // the sheet drives its own Emma (ask, sent, answered)
    if (open) engineRef.current?.chatOpened()
    else engineRef.current?.chatClosed()
  }, [open, variant])

  // Taps anywhere else on screen: she looks at them.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (svgRef.current?.contains(e.target as Node)) return
      engineRef.current?.tapAt(e.clientX, e.clientY)
    }
    document.addEventListener('pointerdown', onDown, { passive: true })
    return () => document.removeEventListener('pointerdown', onDown)
  }, [])

  // Scrolling: pulling the feed down she glances up at it, pushing it up she
  // glances down. Captured at the document so every scroller counts.
  useEffect(() => {
    const last = new WeakMap<EventTarget, number>()
    const onScroll = (e: Event) => {
      const t = e.target as HTMLElement | Document
      const top = t instanceof HTMLElement ? t.scrollTop : window.scrollY
      const prev = last.get(t) ?? top
      last.set(t, top)
      if (Math.abs(top - prev) < 2) return
      engineRef.current?.scroll(top < prev ? 'down' : 'up')
    }
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => document.removeEventListener('scroll', onScroll, { capture: true })
  }, [])

  useEffect(() => emmaBus.on((ev) => {
    if (ev.type === 'lookup') engineRef.current?.lookUp()
    if (ev.type === 'scroll') engineRef.current?.scroll(ev.direction)
    if (ev.type === 'open') setOpen(true)
  }), [setOpen])

  function onTap() {
    // One tap always opens (or closes) the chat. If she was asleep, opening
    // wakes her through the startle sequence first (chatOpened handles it).
    setOpen(!open)
  }

  const body = (
    <svg ref={svgRef} viewBox={variant === 'sheet' ? '90 110 332 370' : '0 0 512 512'} className="emma-svg" aria-hidden="true">
      <path id={`${uid}Body`} fill={fill} d="" />
      <g id={`${uid}Over`} />
      <path id={`${uid}L`} fill="#000" d="" />
      <path id={`${uid}R`} fill="#000" d="" />
      <clipPath id={`${uid}Clip`}><path id={`${uid}ClipPath`} d="" /></clipPath>
      <g clipPath={`url(#${uid}Clip)`}>
        <path id={`${uid}Ridge`} fill={fill} d="" />
        <path id={`${uid}Shade`} fill="none" stroke="#000" strokeWidth={4} strokeLinecap="round" transform="translate(0,2)" d="" />
      </g>
      <CowboyHat id={`${uid}Hat`} />
    </svg>
  )

  if (variant === 'sheet') return <div className="emma-sheet__emma">{body}</div>

  return (
    <button
      className={`${variant === 'tab' ? 'emma-tab' : 'emma-dock'}${open ? ' is-open' : ''}`}
      onClick={onTap}
      aria-label={open ? 'Close the chat with Emma' : 'Chat with Emma'}
      aria-expanded={open}
    >
      {body}
      {ring && <EmmaVoiceRing />}
    </button>
  )
}
